<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { BaseIcon, BaseInputText } from '@cplug/design-system/components'
import { BaseIconName, BaseIconSize, BaseModalType, MediaQuery } from '@cplug/design-system/components/props'
import { MenuSidebar as CoreMenuSidebar, MenuSidebarMobile } from '@cplug/frontend-core/components'
import { useFormModalStore } from '@cplug/frontend-core/stores'
import type { MenuItem } from '@cplug/frontend-core/composables'
import { shouldShowNoveltyBadge } from '../composables/useNoveltyBadge'
import { useStoreSyncSidebarBadge } from '~/domain/dashboard/store/store-sync/composables/useStoreSyncSidebarBadge'
import { useBrandTheme } from '~/domain/shared/branding/composables/useBrandTheme'
import { useMenuOptions } from '~/domain/shared/navigation/composables/useMenuOptions'
import { useAssistantSidebar } from '~/domain/shared/assistant/composables/useAssistantSidebar'
import { useMenuAppContexts } from '~/domain/shared/navigation/composables/useMenuAppContexts'
import BagyIcon from '~/domain/shared/navigation/components/svgs/BagyIcon.vue'
import CplugIcon from '~/domain/shared/navigation/components/svgs/CplugIcon.vue'
import TrayIcon from '~/domain/shared/navigation/components/svgs/TrayIcon.vue'
import BrandLogo from '~/components/BrandLogo.vue'
import SidebarCompanySelect from '~/domain/shared/navigation/components/SidebarCompanySelect.vue'
import useMenuSidebarStore from '~/shared/stores/useMenuSidebarStore'
import ERPSearchPagesModal from '~/domain/shared/navigation/components/organisms/ERPSearchPagesModal.vue'
import SidebarUserMenu from '~/domain/shared/navigation/components/SidebarUserMenu.vue'
import ProductAppContextItem from '~/domain/shared/product-apps/components/ProductAppContextItem.vue'
import { useComingSoonProductMenus } from '~/domain/shared/product-apps/composables/useComingSoonProductMenus'
import { useActiveProductContext } from '~/domain/shared/product-apps/composables/useActiveProductContext'

const menuSidebarStore = useMenuSidebarStore()
const { isOpen, assistantHidesMenu } = storeToRefs(menuSidebarStore)
const formModalStore = useFormModalStore()
const isDesktopViewport = useMediaQuery(MediaQuery.DesktopWithSidebar)
const { shouldHideMenuForAssistant } = useAssistantSidebar()
const { isBagy, isTray } = useBrandTheme()

const { name: productName, isComingSoon } = useActiveProductContext()
const erpMenus = useMenuOptions()
const lojaMenus = useComingSoonProductMenus('loja')
const pagamentosMenus = useComingSoonProductMenus('pagamentos')

const mainMenu = computed(() => {
  if (productName.value === 'loja') {
    return lojaMenus.mainMenu.value
  }
  if (productName.value === 'pagamentos') {
    return pagamentosMenus.mainMenu.value
  }
  return erpMenus.mainMenu.value
})

const salesChannelMenu = computed(() =>
  productName.value === 'erp' ? erpMenus.salesChannelMenu.value : []
)

const bottomMenu = computed(() =>
  productName.value === 'erp' ? erpMenus.bottomMenu.value : []
)
const { appContexts } = useMenuAppContexts()
const { t } = useI18n()
const { shouldShowStoreSyncBadge } = useStoreSyncSidebarBadge()

const desktopSidebarRef = ref<InstanceType<typeof CoreMenuSidebar>>()

/** Drawer mobile quando viewport < 1280 ou assistente compacto esconde menu. */
const useMobileMenuShell = computed(() =>
  !isDesktopViewport.value || shouldHideMenuForAssistant.value
)

const brandLogoComponent = computed(() => {
  if (isBagy.value) {
    return BagyIcon
  }
  if (isTray.value) {
    return TrayIcon
  }
  return CplugIcon
})

function openSearchModal (): void {
  formModalStore.open({
    title: t('SearchModalTitle'),
    content: ERPSearchPagesModal,
    readonly: true,
    type: BaseModalType.Default
  })
}

function shouldShowBadge (item: MenuItem): boolean {
  const itemPath = (item as any).path || ''
  return itemPath ? shouldShowNoveltyBadge(itemPath) : false
}

function appContextIconName (item: MenuItem): BaseIconName {
  if (item.name === 'store') {
    return BaseIconName.Shop
  }

  return item.icon as BaseIconName ?? BaseIconName.Desktop
}

const totalSidebarWidth = computed(() => {
  if (useMobileMenuShell.value || assistantHidesMenu.value) {
    return 0
  }

  return desktopSidebarRef.value?.totalSidebarWidth || 200
})

function onKeydown (event: KeyboardEvent): void {
  if (event.ctrlKey && event.key === 'k') {
    event.preventDefault()
    openSearchModal()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

defineExpose({
  totalSidebarWidth
})
</script>

<template>
  <MenuSidebarMobile
    v-if="useMobileMenuShell"
    v-model="isOpen"
    :main-menu="mainMenu"
    :sales-channel-menu="salesChannelMenu"
    :bottom-menu="bottomMenu"
  >
    <template #logo>
      <BrandLogo class="h-[51px]!" size="84px" />
    </template>
    <template #search>
      <div class="w-full cursor-pointer px-4 pt-4" tabindex="-1" @click="openSearchModal">
        <BaseInputText
          size="small"
          :placeholder="t('SearchPlaceholder')"
          :icon-prefix="BaseIconName.Search"
          class="w-full pointer-events-none"
        />
      </div>
    </template>
    <template #badge="{ item }">
      <span
        v-if="shouldShowBadge(item)"
        class="w-2 h-2 bg-red-500 rounded-full shrink-0"
      />
    </template>
  </MenuSidebarMobile>

  <CoreMenuSidebar
    v-else
    ref="desktopSidebarRef"
    v-model="isOpen"
    :app-contexts="appContexts"
    :main-menu="mainMenu"
    :sales-channel-menu="salesChannelMenu"
    :bottom-menu="bottomMenu"
  >
    <template #primary-rail-brand>
      <component
        :is="brandLogoComponent"
        class="menu-sidebar-primary-rail-brand pointer-events-none block w-full max-w-full select-none"
        aria-hidden="true"
      />
    </template>
    <template #app-context-icon="{ item }">
      <span
        v-if="item.name === 'store'"
        class="relative flex h-6 w-6 shrink-0 items-center justify-center"
        data-testid="store-app-context-icon"
      >
        <BaseIcon
          :name="appContextIconName(item)"
          :size="BaseIconSize.Small"
        />
        <span
          v-if="shouldShowStoreSyncBadge(item)"
          class="absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-amber-400"
          :title="t('StoreSyncPendingBadge')"
        />
      </span>
      <ProductAppContextItem v-else :item="item" />
    </template>
    <template #logo>
      <BrandLogo class="h-10!" size="84px" />
    </template>
    <template #company-selector>
      <SidebarCompanySelect />
    </template>
    <template v-if="!isComingSoon" #search>
      <div class="w-full cursor-pointer" tabindex="-1" @click="openSearchModal">
        <BaseInputText
          size="small"
          :placeholder="t('SearchPlaceholder')"
          :icon-prefix="BaseIconName.Search"
          class="pointer-events-none w-full"
        />
      </div>
    </template>
    <template #badge="{ item }">
      <span
        v-if="shouldShowStoreSyncBadge(item)"
        class="w-2 h-2 bg-amber-400 rounded-full shrink-0"
        :title="t('StoreSyncPendingBadge')"
      />
      <span
        v-else-if="shouldShowBadge(item)"
        class="h-2 w-2 shrink-0 rounded-full bg-destructive"
      />
    </template>

    <template #primary-rail-footer>
      <SidebarUserMenu />
    </template>
  </CoreMenuSidebar>
</template>

<i18n lang="yaml">
pt-br:
  SearchPlaceholder: 'Buscar no menu...'
  SearchModalTitle: 'Buscar no menu'
  StoreSyncPendingBadge: Cardápio aguardando publicação manual
  ErpApp: ERP
  OnlineStoreApp: Loja Online

en:
  SearchPlaceholder: 'Search the menu...'
  SearchModalTitle: 'Search the menu'
  StoreSyncPendingBadge: Menu waiting for manual publish
  ErpApp: ERP
  OnlineStoreApp: Online Store

en-US:
  StoreSyncPendingBadge: Menu waiting for manual publish
  ErpApp: ERP
  OnlineStoreApp: Online Store

es:
  SearchPlaceholder: 'Buscar en el menú...'
  SearchModalTitle: 'Buscar en el menú'
  StoreSyncPendingBadge: Menú pendiente de publicación manual
  ErpApp: ERP
  OnlineStoreApp: Tienda Online
</i18n>

<style scoped>
.menu-sidebar-primary-rail-brand :deep(svg),
.menu-sidebar-primary-rail-brand :deep(.bagy-logo),
.menu-sidebar-primary-rail-brand :deep(.tray-logo) {
  display: block;
  width: 100%;
  height: auto;
  max-height: 100%;
}
</style>
